import assertNever from '@watershed/shared-util/src/assertNever';
import { INGESTION_CUSTOM_FIELD_PREFIX } from '@watershed/constants/customFields';
import { singleQuote } from '../../sql';
import { joinTickedWithAnd } from '../../utils/helpers';
import { assertConstantValue } from '../assert';
import { ASTCall } from '../ast';
import { ExprType, makeNullable } from '../expr_types';
import { extractStringLiteral } from '../literal';
import { assertNonStructColumnType } from '../row_types';
import { maybeAddInputVariable, traceHelper } from '../sql/internal';
import { TraceASTInput, TraceASTType } from '../trace';
import {
  assertArgShape,
  assertKwargShape,
  TypecheckWarning,
  typecheckExpression,
} from '../typecheck';
import { Context, Func, SqlExpression } from '../types';

// Function for looking up a column in the input type.
//
// This function is similar to the bart function, but it returns a nullable
// type. This is useful for cases where the column may not be present in the
// input. For example, we cannot guarantee that a custom column will be present
// in the input, so we need to return a Nullable<type> for the custom column.
//
// TODO [CPT-6941]: remove special handling for custom columns as a special case once
// custom columns are directly available in the input type
export const inputSilent: Func = {
  typecheck(
    ctx: Context,
    call: ASTCall,
    warnings?: Array<TypecheckWarning>
  ): ExprType {
    assertArgShape(call, 1, 1);
    assertKwargShape(call, [], false, warnings, undefined);

    // It's important that it's a literal, since we use static analysis to
    // figure out which input columns a given expression uses.
    const field = extractStringLiteral(call.args[0]);

    // handle custom columns as a special case
    // TODO [CPT-6941]: remove special handling
    if (field.startsWith(INGESTION_CUSTOM_FIELD_PREFIX)) {
      const customFieldName = field.slice(INGESTION_CUSTOM_FIELD_PREFIX.length);
      const tagsColumnType = ctx.inputType.get('tags');

      // if tags column does not exist, throw an error
      if (!tagsColumnType) {
        throw new Error(`Tags column should exist but got undefined`);
      }

      // tags column should be a struct type
      if (tagsColumnType.ty !== 'struct') {
        throw new Error(
          `Tags column should be a struct but got type: ${tagsColumnType.ty}`
        );
      }

      const customFieldType = tagsColumnType.fields.get(customFieldName);
      if (!customFieldType) {
        const known = joinTickedWithAnd(tagsColumnType.fields.keys());
        warnings?.push(
          `Unknown custom column \`${customFieldName}\` (know about ${known}): assuming type 'string'.`
        );
        return makeNullable({ ty: 'string' });
      }

      // custom columns must be a non-struct type
      assertNonStructColumnType(customFieldType);

      return makeNullable(customFieldType);
    }

    const columnType = ctx.inputType.get(field);

    // if the field does not exist, we'll assume it's a nullable string and add
    // a warning
    if (!columnType) {
      const known = joinTickedWithAnd(ctx.inputType.keys());
      warnings?.push(
        `Unknown BART column \`${field}\` (know about ${known}): assuming type 'string'.`
      );
      return makeNullable({ ty: 'string' });
    }

    // This looks useless, but we're dropping an extra "column" field from
    // RowType
    if (columnType.ty === 'quantity') {
      return makeNullable({ ty: 'quantity', units: columnType.units });
    }

    // column type should be a non-struct type
    assertNonStructColumnType(columnType);

    return makeNullable(columnType);
  },

  eval(ctx: Context, call: ASTCall): unknown {
    const field = extractStringLiteral(call.args[0]);

    // handle custom columns as a special case
    // TODO [CPT-6941]: remove special handling
    if (field.startsWith(INGESTION_CUSTOM_FIELD_PREFIX)) {
      const customFieldName = field.slice(INGESTION_CUSTOM_FIELD_PREFIX.length);
      const tagsColumn = ctx.input.get('tags');
      if (tagsColumn && typeof tagsColumn === 'object') {
        const value = (tagsColumn as Record<string, unknown>)[customFieldName];
        if (value !== undefined && value !== null) {
          return assertConstantValue(value);
        }
      }
      // If tags column doesn't exist or custom field not found, return null
      return null;
    }

    const v = ctx.input.get(field);
    if (v !== undefined && v !== null) {
      return assertConstantValue(v);
    }

    return null;
  },

  toSql(ctx: Context, call: ASTCall): SqlExpression {
    const ty = typecheckExpression(ctx, call);
    const name = extractStringLiteral(call.args[0]);

    const duckDbType = exprTypeToDuckDbType(ty);

    // generate sql for variable and handle custom columns as a special case
    // TODO [CPT-6941]: remove special handling
    const columnSql = (() => {
      if (name.startsWith(INGESTION_CUSTOM_FIELD_PREFIX)) {
        const customFieldName = name.slice(
          INGESTION_CUSTOM_FIELD_PREFIX.length
        );
        const tagsColumnSql = maybeAddInputVariable(ctx, 'tags', 'JSON');
        const typeMap = JSON.stringify({ [customFieldName]: duckDbType });
        return `struct_extract(json_transform(${tagsColumnSql}, '${typeMap}'), ${singleQuote(customFieldName)})`;
      } else {
        return maybeAddInputVariable(ctx, name, duckDbType);
      }
    })();

    return traceHelper<TraceASTInput>(
      ty,
      {
        expr: columnSql,
        stage: 0,
        unitExpr: null,
        error: null,
      },
      () => ({
        t: TraceASTType.TInput,
        c: name,
      })
    );
  },
};

/**
 * Maps ExprType to DuckDB SQL type for json_transform operations. Handles
 * primitive types, nullable types, and falls back to VARCHAR for complex types,
 * but those aren't allowed as return types for column function anyway.
 */
function exprTypeToDuckDbType(ty: ExprType): string {
  switch (ty.ty) {
    case 'string':
      return 'VARCHAR';
    case 'boolean':
      return 'BOOLEAN';
    case 'number':
    case 'quantity':
      return 'DOUBLE';
    case 'null':
      return 'VARCHAR'; // Default to VARCHAR for null
    case 'nullable':
      return exprTypeToDuckDbType(ty.baseTy);
    case 'list':
      // For lists, we'll use VARCHAR as a fallback since JSON arrays are
      // complex
      return 'VARCHAR';
    case 'date':
      return 'DATE';
    case 'datetime':
      return 'TIMESTAMP';
    default:
      assertNever(ty);
  }
}

export const exportedForTesting = {
  exprTypeToDuckDbType,
};
